/**
 * Auth Service — provider-agnostic interface for authentication.
 *
 * This is the only file in the codebase that imports from @clerk/clerk-react.
 * All other files use the exports here. Swapping providers means updating
 * this file only (and App.jsx for the provider/gating JSX components).
 */

import { useUser, useClerk } from '@clerk/clerk-react';

/**
 * Hook: current authenticated user in a provider-agnostic shape.
 * Returns null if the user is not signed in or not yet loaded.
 */
export const useCurrentUser = () => {
  const { user } = useUser();
  if (!user) return null;
  return {
    sub: user.id,
    email: user.primaryEmailAddress?.emailAddress ?? null,
    displayName: user.fullName || user.firstName || null,
  };
};

/**
 * Hook: sign-out function.
 */
export const useSignOut = () => {
  const { signOut } = useClerk();
  return signOut;
};

/**
 * Get the current session's Bearer token.
 * Uses window.Clerk global — safe to call outside React components.
 * Clerk auto-refreshes tokens; this always returns a fresh token.
 *
 * @returns {Promise<string|null>}
 */
export const getToken = async () => {
  return window.Clerk?.session?.getToken({ template: 'aws-apigw' }) ?? null;
};

/**
 * Authenticated fetch — drop-in replacement for the old
 * authService.authenticatedFetch(url, options) pattern.
 * Attaches Authorization: Bearer header automatically.
 *
 * @param {string} url
 * @param {RequestInit} options
 * @returns {Promise<Response>}
 */
export const authenticatedFetch = async (url, options = {}) => {
  const token = await getToken();
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
};
